import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { X, Truck } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { updateCourierThunk } from '../../../../features/couriers/couriersThunks';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';

const COURIER_SERVICES_TEMPLATES = {
  FEDEX: {
    FEDEX_PRIORITY: { min_weight: "0", max_weight: "" },
    FEDEX_PRIORITY_FREIGHT: { min_weight: "", max_weight: "" },
  },
};

const EditCourierDialog = ({ onClose, initialData }) => {
  const dispatch = useDispatch();
  const { templates } = useSelector((state) => state.couriers);
  // The save itself, not the slice's `loading`: that one is also on while the
  // list behind the dialog reloads.
  const saving = useSelector(selectIsRunning(updateCourierThunk));

  // Read once: the credentials typed here are password fields, and a refetch
  // of the templates used to put the saved values back unnoticed.
  const [formData, setFormData] = useState(() => {
    const initialServicesTemplate = COURIER_SERVICES_TEMPLATES[initialData.name]
      ? JSON.parse(JSON.stringify(COURIER_SERVICES_TEMPLATES[initialData.name]))
      : {};

    return {
      id: initialData.id,
      name: initialData.name,
      configuration: { ...(initialData.configuration || {}) },
      services: {
        ...initialServicesTemplate,
        ...(initialData.services || {}),
      },
    };
  });
  const [formErrors, setFormErrors] = useState({});

  // Merged at render rather than stored, because the templates can arrive after
  // opening: they fill in the fields they define, and saved or typed values win.
  const configuration = { ...(templates[formData.name] || {}), ...formData.configuration };

  const isFormValid =
    formData.name.trim() !== "" &&
    Object.keys(templates[formData.name] || {}).every(
      (key) => configuration[key] && String(configuration[key]).trim() !== ""
    ) &&
    (formData.name !== "FEDEX" ||
      Object.keys(formData.services || {}).every(
        (serviceKey) =>
          formData.services[serviceKey]?.min_weight !== "" &&
          formData.services[serviceKey]?.max_weight !== ""
      ));

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      configuration: { ...prev.configuration, [name]: value },
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleServiceWeightChange = (serviceKey, field, value) => {
    const sanitizedValue = String(value).replace(/-/g, "");
    setFormData((prev) => ({
      ...prev,
      services: {
        ...prev.services,
        [serviceKey]: {
          ...prev.services[serviceKey],
          [field]: sanitizedValue,
        },
      },
    }));

    if (formErrors[`${serviceKey}_${field}`]) {
      setFormErrors((prev) => ({ ...prev, [`${serviceKey}_${field}`]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name) {
      errors.submit = "Datos de proveedor inválidos";
      setFormErrors(errors);
      return false;
    }

    const requiredFields = Object.keys(templates[formData.name] || {});
    requiredFields.forEach((field) => {
      if (!configuration[field] || String(configuration[field]).trim() === "") {
        errors[field] = "Este campo es requerido";
      }
    });

    if (formData.name === "FEDEX" && formData.services) {
      const priorityMin = parseFloat(formData.services.FEDEX_PRIORITY?.min_weight);
      const priorityMax = parseFloat(formData.services.FEDEX_PRIORITY?.max_weight);
      const freightMin = parseFloat(formData.services.FEDEX_PRIORITY_FREIGHT?.min_weight);
      const freightMax = parseFloat(formData.services.FEDEX_PRIORITY_FREIGHT?.max_weight);

      if (isNaN(priorityMin) || priorityMin < 0) errors.FEDEX_PRIORITY_min_weight = "Mínimo inválido";
      if (isNaN(priorityMax) || priorityMax <= priorityMin) errors.FEDEX_PRIORITY_max_weight = "Debe ser mayor al mínimo";
      if (isNaN(freightMin) || freightMin < 0) errors.FEDEX_FREIGHT_min_weight = "Mínimo inválido";
      if (isNaN(freightMax) || freightMax <= freightMin) errors.FEDEX_FREIGHT_max_weight = "Debe ser mayor al mínimo";

      if (!errors.FEDEX_PRIORITY_max_weight && !errors.FEDEX_FREIGHT_min_weight) {
        if (
          (priorityMin <= freightMin && priorityMax >= freightMin) ||
          (freightMin <= priorityMin && freightMax >= priorityMin)
        ) {
          errors.submit = "Los rangos de peso no pueden solaparse ni tener valores iguales.";
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      configuration,
      ...(formData.name === 'FEDEX' && Object.keys(formData.services).length > 0 && { services: formData.services })
    };

    dispatch(updateCourierThunk({ courierId: formData.id, courierData: payload, isStaff: true }))
      .unwrap()
      .then(() => {
        onClose();
      })
      .catch((err) => {
        console.error("Error al actualizar:", err);
      });
  };

  const formatLabel = (key) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const isSecretField = (key) => {
    const lowerKey = key.toLowerCase();
    return lowerKey.includes("secret") || lowerKey.includes("password") || lowerKey.includes("token") || lowerKey.includes("clave");
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200 p-4 overflow-y-auto">
      <div
        role="dialog"
        aria-modal="true"
        data-testid="edit-courier-modal"
        className="bg-white dark:bg-[#1a1a1a] w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 my-auto">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-white/90 dark:bg-[#1a1a1a]/90 backdrop-blur-md z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-ninja-green/10 flex items-center justify-center text-ninja-green">
              <Truck size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-ninja-dark dark:text-white">Editar Proveedor Externo</h2>
              <p className="text-sm text-text-secondary">{formData.name}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Cerrar"
            className="p-2 rounded-full hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] text-text-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <form id="edit-courier-form" onSubmit={handleSubmit} className="space-y-6">

            {/* Config Fields */}
            {formData.name && templates[formData.name] ? (
              <div className="bg-ninja-gray/30 dark:bg-[#222] p-5 rounded-2xl space-y-4">
                <h3 className="font-bold text-ninja-dark dark:text-white text-sm">Configuración de Credenciales</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.keys(templates[formData.name]).map((fieldKey) => (
                    <div key={fieldKey} className="space-y-1.5">
                      <label className="text-xs font-semibold text-text-secondary">{formatLabel(fieldKey)} *</label>
                      <input
                        type={isSecretField(fieldKey) ? "password" : "text"}
                        name={fieldKey}
                        value={configuration[fieldKey] || ""}
                        onChange={handleConfigChange}
                        required
                        className={`w-full px-3 py-2 bg-white dark:bg-[#111] border ${formErrors[fieldKey] ? 'border-danger focus:ring-danger' : 'border-border focus:ring-ninja-green'} rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all dark:text-white text-sm`}
                      />
                      {formErrors[fieldKey] && <p className="text-xs text-danger">{formErrors[fieldKey]}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-800 rounded-xl text-sm">
                No se encontraron plantillas de configuración para este proveedor.
              </div>
            )}

            {/* FEDEX Services */}
            {formData.name === "FEDEX" && formData.services && (
              <div className="border border-dashed border-border p-5 rounded-2xl bg-ninja-gray/10 dark:bg-[#1a1a1a] space-y-4">
                <h3 className="font-bold text-ninja-green text-sm">Enrutamiento Automático por Peso</h3>

                {Object.keys(formData.services).map((serviceKey) => (
                  <div key={serviceKey} className="space-y-2">
                    <h4 className="text-sm font-semibold text-ninja-dark dark:text-white">
                      {serviceKey.replaceAll("_", " ")}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-text-secondary mb-1 block">Peso Mínimo (kg)</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={formData.services[serviceKey].min_weight}
                          onChange={(e) => handleServiceWeightChange(serviceKey, "min_weight", e.target.value)}
                          required
                          className={`w-full px-3 py-2 bg-white dark:bg-[#111] border ${formErrors[`${serviceKey}_min_weight`] ? 'border-danger focus:ring-danger' : 'border-border focus:ring-ninja-green'} rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all dark:text-white text-sm`}
                        />
                        {formErrors[`${serviceKey}_min_weight`] && <p className="text-xs text-danger mt-1">{formErrors[`${serviceKey}_min_weight`]}</p>}
                      </div>
                      <div>
                        <label className="text-xs text-text-secondary mb-1 block">Peso Máximo (kg)</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={formData.services[serviceKey].max_weight}
                          onChange={(e) => handleServiceWeightChange(serviceKey, "max_weight", e.target.value)}
                          required
                          className={`w-full px-3 py-2 bg-white dark:bg-[#111] border ${formErrors[`${serviceKey}_max_weight`] ? 'border-danger focus:ring-danger' : 'border-border focus:ring-ninja-green'} rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all dark:text-white text-sm`}
                        />
                        {formErrors[`${serviceKey}_max_weight`] && <p className="text-xs text-danger mt-1">{formErrors[`${serviceKey}_max_weight`]}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {formErrors.submit && (
              <div className="bg-red-50 dark:bg-red-900/20 text-danger p-4 rounded-xl border border-red-200 dark:border-red-900 text-sm font-medium">
                {formErrors.submit}
              </div>
            )}
          </form>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border flex gap-4 bg-ninja-gray/20 dark:bg-[#151515]">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 font-bold text-ninja-dark dark:text-white hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={saving}
          >
            Cancelar
          </button>
          <Button
            variant="primary"
            size="lg"
            form="edit-courier-form"
            type="submit"
            disabled={!isFormValid}
            loading={saving}
            loadingLabel="Guardando..."
            className="flex-1"
          >
            Guardar Cambios
          </Button>
        </div>

      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

/**
 * Mounted only while open: each opening starts from the courier as it is then,
 * and nothing that arrives while it is open overwrites the form.
 */
export const EditCourierModal = (props) =>
  props.isOpen && props.initialData ? <EditCourierDialog {...props} /> : null;
